import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Construction } from 'lucide-react';
import { cn } from '@/utils/cn';

interface StaggeredMenuItem {
  label: string;
  onClick?: () => void;
}

interface StaggeredMenuProps {
  isOpen: boolean;
  onClose: () => void;
  items: StaggeredMenuItem[];
  position?: 'left' | 'right';
  colors?: string[];
  displayItemNumbering?: boolean;
  accentColor?: string;
  menuButtonColor?: string;
  className?: string;
}

export function StaggeredMenu({
  isOpen,
  onClose,
  items,
  position = 'left',
  colors = ['#B19EEF', '#5227FF'],
  displayItemNumbering = true,
  accentColor = '#515152',
  menuButtonColor = '#fcf7f7',
  className,
}: StaggeredMenuProps) {
  const menuVariants = {
    closed: {
      x: position === 'left' ? '-100%' : '100%',
      transition: {
        type: 'spring',
        stiffness: 400,
        damping: 40,
      },
    },
    open: {
      x: 0,
      transition: {
        type: 'spring',
        stiffness: 400,
        damping: 40,
      },
    },
  };

  const itemVariants = {
    closed: { opacity: 0, x: -20 },
    open: (i: number) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: i * 0.1,
        type: 'spring',
        stiffness: 300,
        damping: 24,
      },
    }),
  };

  const layerVariants = (index: number) => ({
    closed: {
      x: position === 'left' ? '-100%' : '100%',
      transition: {
        delay: index * 0.05,
        type: 'spring',
        stiffness: 400,
        damping: 40,
      },
    },
    open: {
      x: 0,
      transition: {
        delay: index * 0.05,
        type: 'spring',
        stiffness: 400,
        damping: 40,
      },
    },
  });

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/30 z-40"
            onClick={onClose}
          />

          <div
            className={cn(
              'fixed top-0 bottom-0 z-50 flex',
              position === 'left' ? 'left-0' : 'right-0',
              className
            )}
          >
            {colors.map((color, index) => (
              <motion.div
                key={color}
                variants={layerVariants(index)}
                initial="closed"
                animate="open"
                exit="closed"
                style={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: position === 'left' ? 0 : 'auto',
                  right: position === 'right' ? 0 : 'auto',
                  width: 320 - index * 20,
                  backgroundColor: color,
                  zIndex: colors.length - index,
                }}
                className="rounded-r-3xl"
              />
            ))}

            <motion.div
              variants={menuVariants}
              initial="closed"
              animate="open"
              exit="closed"
              className="relative z-[100] w-80 h-full"
              style={{
                backgroundColor: menuButtonColor,
              }}
            >
              <div className="p-8 h-full flex flex-col">
                <div className="flex justify-end mb-8">
                  <button
                    onClick={onClose}
                    className="p-2 rounded-full hover:bg-gray-200 transition-colors"
                  >
                    <X className="w-6 h-6 text-gray-600" />
                  </button>
                </div>

                <nav className="flex-1 overflow-y-auto">
                  <ul className="space-y-2">
                    {items.map((item, index) => (
                      <motion.li
                        key={item.label}
                        custom={index}
                        variants={itemVariants}
                        initial="closed"
                        animate="open"
                        exit="closed"
                      >
                        <button
                          onClick={() => {
                            item.onClick?.();
                            onClose();
                          }}
                          className="w-full text-left px-4 py-4 rounded-xl text-lg font-medium text-gray-700 hover:text-white transition-colors group relative overflow-hidden"
                          style={{
                            '--accent-color': accentColor,
                          } as React.CSSProperties}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = accentColor;
                            e.currentTarget.style.color = 'white';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = '';
                          }}
                        >
                          <span className="flex items-center gap-4">
                            {displayItemNumbering && (
                              <span className="text-sm opacity-60 font-mono">
                                {String(index + 1).padStart(2, '0')}
                              </span>
                            )}
                            <span>{item.label}</span>
                          </span>
                        </button>
                      </motion.li>
                    ))}
                  </ul>
                </nav>

                <div className="pt-8 border-t border-gray-200">
                  <p className="text-sm text-gray-400 text-center">
                    Project Timing Dashboard
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
